import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import BackgroundIcons from '../../components/BackgroundIcons';
import CustomEventModal from '../../components/CustomEventModal';
import UnifiedSelectorModal from '../../components/UnifiedSelectorModal';
import DraggableTimeline from '../../components/DraggableTimeline';
import SaveTimelineModal from '../../components/SaveTimelineModal';
import EditEventModal from '../../components/EditEventModal';
import NotificationModal from '../../components/NotificationModal';
import { apiService } from '../../services/apiService';
import { generateTimelinePDF } from '../../utils/pdfGenerator';

const TimelineGenerator = () => {
  const location = useLocation();
  const editingTimeline = location.state?.timeline;
  
  const [formData, setFormData] = useState({
    weddingDate: '',
    ceremonyTime: '',
    receptionDate: '',
    receptionTime: '',
    guestCount: '',
    venue: '',
    ceremonyType: 'traditional',
    venueType: 'single',
    travelTime: 30,
    hasPhotography: true,
    hasReception: true,
    hasCocktailHour: true,
    additionalEvents: ''
  });
  const [bufferSettings, setBufferSettings] = useState({
    photography: 15,
    travel: 30,
    setup: 20
  });
  const [timeline, setTimeline] = useState([]);
  const [stagedEvents, setStagedEvents] = useState([]); // Events selected but not yet generated
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCustomEventModal, setShowCustomEventModal] = useState(false);
  const [showUnifiedModal, setShowUnifiedModal] = useState({ show: false, type: null });
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'info' });

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Load timeline data if editing
  useEffect(() => {
    if (editingTimeline) {
      const events = typeof editingTimeline.timeline_events === 'string' 
        ? JSON.parse(editingTimeline.timeline_events) 
        : editingTimeline.timeline_events;
      
      setFormData({
        weddingDate: editingTimeline.wedding_date ? editingTimeline.wedding_date.split('T')[0] : '',
        ceremonyTime: editingTimeline.ceremony_time || '',
        receptionDate: editingTimeline.reception_date ? editingTimeline.reception_date.split('T')[0] : '',
        receptionTime: editingTimeline.reception_time || '',
        guestCount: editingTimeline.guest_count || '',
        venue: editingTimeline.metadata?.venue || '',
        ceremonyType: editingTimeline.ceremony_type || 'traditional',
        venueType: editingTimeline.venue_type || 'single',
        travelTime: editingTimeline.metadata?.travel_time || 30,
        hasPhotography: editingTimeline.metadata?.has_photography !== false,
        hasReception: editingTimeline.metadata?.has_reception !== false,
        hasCocktailHour: editingTimeline.metadata?.has_cocktail_hour !== false
      });
      
      setTimeline(events);
      
      // Pre-populate staged events from timeline
      setStagedEvents(events.map(event => ({
        event: event.event,
        description: event.description,
        icon: event.icon,
        duration: event.duration || 30,
        isRitual: event.isRitual || false,
        isCustom: event.isCustom || false,
        ritualId: event.ritualId || null,
        culture_display_name: event.culture_display_name || null
      })));
    }
  }, [editingTimeline]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const generateTimeline = async (data = formData) => {
    // Validate ceremony time
    if (!data.ceremonyTime) {
      setNotification({
        show: true,
        title: 'Missing Information',
        message: 'Please enter ceremony time',
        type: 'error'
      });
      return;
    }
    
    // Validate that user has selected events/rituals
    if (stagedEvents.length === 0) {
      setNotification({
        show: true,
        title: 'No Events Selected',
        message: 'Please add events or rituals from the selection buttons above to create your timeline',
        type: 'error'
      });
      return;
    }
    
    setLoading(true);
    
    // Check if ceremony and reception are on different days
    const isDifferentDays = data.receptionDate && data.weddingDate && data.receptionDate !== data.weddingDate;
    
    // Simulate timeline generation with async
    setTimeout(async () => {
      // Only use user-selected events/rituals
      const allEventsForOrdering = stagedEvents;
      
      // Use AI to order events WITH timing intelligence
      let orderedAllEvents = allEventsForOrdering;
      if (allEventsForOrdering.length > 1) {
        try {
          const response = await apiService.userTimelines.orderEvents({
            events: allEventsForOrdering,
            weddingDate: data.weddingDate,
            ceremonyTime: data.ceremonyTime,
            receptionDate: data.receptionDate || null,
            receptionTime: data.receptionTime || null
          });
          if (response.data.success && response.data.orderedEvents) {
            orderedAllEvents = response.data.orderedEvents;
            console.log('✅ Events ordered by AI with intelligent timing');
          } else {
            // AI failed
            setLoading(false);
            setNotification({
              show: true,
              title: 'AI Service Unavailable',
              message: response.data.error || 'AI models are currently unavailable. Please try again in a moment.',
              type: 'error'
            });
            return;
          }
        } catch (error) {
          console.error('AI ordering failed:', error);
          setLoading(false);
          setNotification({
            show: true,
            title: 'AI Service Unavailable',
            message: 'AI models are currently unavailable. Please try again in a moment.',
            type: 'error'
          });
          return;
        }
      }
      
      // AI has already assigned times and dates, use them directly
      const allEventsWithTimes = orderedAllEvents.map((event) => {
        // Normalize dates for comparison (remove any time component)
        const eventDate = event.date?.split('T')[0];
        const weddingDate = data.weddingDate?.split('T')[0];
        const receptionDate = data.receptionDate?.split('T')[0];
        
        // Determine date label based on actual event date
        let dateLabel = null;
        if (isDifferentDays) {
          if (eventDate === weddingDate) {
            dateLabel = 'Wedding Day';
          } else if (eventDate === receptionDate) {
            dateLabel = 'Reception Day';
          } else if (eventDate < weddingDate) {
            dateLabel = 'Pre-Wedding Events';
          }
        } else {
          // Single day wedding
          if (eventDate === weddingDate) {
            dateLabel = 'Wedding Day';
          } else if (eventDate < weddingDate) {
            dateLabel = 'Pre-Wedding Events';
          }
        }
        
        return {
          ...event,
          date: eventDate, // Store normalized date
          dateLabel
        };
      });
      
      // Events already in correct order with AI-assigned times
      setTimeline(allEventsWithTimes);
      setLoading(false);
    }, 800);
  };

  const downloadPDF = () => {
    generateTimelinePDF(timeline, null, formData.weddingDate);
  };

  const addToPlaybook = () => {
    setNotification({
      show: true,
      message: 'Timeline has been added to your Big Day Playbook!',
      type: 'success'
    });
  };

  const clearForm = () => {
    setFormData({
      weddingDate: '',
      ceremonyTime: '',
      receptionDate: '',
      receptionTime: '',
      guestCount: '',
      venue: '',
      ceremonyType: 'traditional',
      venueType: 'single',
      travelTime: 30,
      hasPhotography: true,
      hasReception: true,
      hasCocktailHour: true,
      additionalEvents: ''
    });
    setBufferSettings({
      photography: 15,
      travel: 30,
      setup: 20
    });
    setTimeline([]);
    setStagedEvents([]);
  };

  const handleAddCustomEvent = (eventData) => {
    const newEvent = {
      time: eventData.startTime,
      event: eventData.name,
      description: eventData.description || `Duration: ${eventData.duration} minutes`,
      icon: eventData.icon,
      category: eventData.category,
      duration: eventData.duration,
      bufferBefore: eventData.bufferBefore,
      bufferAfter: eventData.bufferAfter,
      isCustom: true
    };
    
    setStagedEvents(prev => [...prev, newEvent]);
  };

  const handleAddFromUnifiedModal = (selectedItems) => {
    // Remove existing items that match the selected items' IDs
    const selectedIds = selectedItems.map(item => item.ritual_id || item.id);
    const filteredStaged = stagedEvents.filter(event => 
      !selectedIds.includes(event.ritualId) && !selectedIds.includes(event.id)
    );
    
    // Map new events (without time - will be calculated during generation)
    const newEvents = selectedItems.map(item => ({
      event: item.name,
      description: item.description || '',
      icon: item.icon || '🎉',
      duration: item.duration_minutes || item.duration || 30,
      isRitual: !!item.culture_display_name,
      ritualId: item.ritual_id || item.id,
      id: item.ritual_id || item.id,
      culture_display_name: item.culture_display_name
    }));
    
    // Replace with new selection
    setStagedEvents([...filteredStaged, ...newEvents]);
  };

  const parseDuration = (durationStr) => {
    if (!durationStr) return 30;
    const match = durationStr.match(/(\d+)/);
    return match ? parseInt(match[1]) : 30;
  };

  const handleReorderTimeline = (newEvents) => {
    setTimeline(newEvents);
  };

  const handleEditEvent = (event, index) => {
    setEditingEvent(event);
    setEditingIndex(index);
    setShowEditModal(true);
  };

  const handleSaveEdit = (updatedEvent) => {
    setTimeline(prev => {
      const updated = [...prev];
      updated[editingIndex] = {
        ...updated[editingIndex],
        ...updatedEvent
      };
      return updated;
    });
    setEditingEvent(null);
    setEditingIndex(null);
  };

  const handleDeleteEvent = (index) => {
    setDeleteIndex(index);
    setNotification({
      show: true,
      message: 'Are you sure you want to delete this event?',
      type: 'warning',
      actionText: 'Delete',
      onAction: () => {
        setTimeline(prev => prev.filter((_, i) => i !== index));
        setDeleteIndex(null);
        setNotification({ show: false, message: '', type: 'info' });
      }
    });
  };

  const checkLoginAndProceed = (action) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setNotification({
        show: true,
        title: 'Login Required',
        message: 'Please log in to save your timeline',
        type: 'warning',
        actionLabel: 'Log In',
        onAction: () => window.dispatchEvent(new Event('openAuthModal'))
      });
      return false;
    }
    return true;
  };

  const handleSaveClick = () => {
    if (checkLoginAndProceed('save')) {
      setShowSaveModal(true);
    }
  };

  const handleViewSavedClick = () => {
    if (checkLoginAndProceed('view')) {
      window.open('/dashboard/timeline', '_blank');
    }
  };

  const handleSaveTimeline = async (timelineName) => {
    try {
      const timelineData = {
        timeline_name: timelineName,
        wedding_date: formData.weddingDate,
        ceremony_time: formData.ceremonyTime,
        reception_date: formData.receptionDate || null,
        reception_time: formData.receptionTime || null,
        ceremony_type: formData.ceremonyType,
        venue_type: formData.venueType,
        guest_count: formData.guestCount ? parseInt(formData.guestCount) : null,
        timeline_events: timeline,
        selected_rituals: timeline.filter(e => e.isRitual).map(e => ({ id: e.ritualId })),
        buffer_settings: bufferSettings,
        metadata: {
          venue: formData.venue,
          travel_time: formData.travelTime,
          has_photography: formData.hasPhotography,
          has_reception: formData.hasReception,
          has_cocktail_hour: formData.hasCocktailHour
        }
      };

      await apiService.userTimelines.save(timelineData);
      setNotification({
        show: true,
        message: '✅ Timeline saved successfully!',
        type: 'success'
      });
    } catch (error) {
      console.error('Error saving timeline:', error);
      setNotification({
        show: true,
        message: 'Failed to save timeline. Please try again.',
        type: 'error'
      });
      throw error;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'preparation': return 'from-rose-100 to-pink-200';
      case 'photography': return 'from-purple-100 to-lavender-200';
      case 'ceremony': return 'from-gold-100 to-yellow-200';
      case 'reception': return 'from-emerald-100 to-mint-200';
      default: return 'from-gray-100 to-gray-200';
    }
  };  const pageIcons = [
    { emoji: '📅', size: '6xl', opacity: '15' },
    { emoji: '⏰', size: '4xl', opacity: '15' },
    { emoji: '🕐', size: '8xl', opacity: '15' },
    { emoji: '📋', size: '6xl', opacity: '15' },
    { emoji: '💖', size: '4xl', opacity: '15' }
  ];



  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 relative overflow-hidden">
      <BackgroundIcons icons={pageIcons} />
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating sparkles */}
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-pulse opacity-10"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 2}s`
            }}
          >
            <span className="text-gold-400 text-lg">✨</span>
          </div>
        ))}

      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 relative z-10">
        
        {/* Elegant Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 rounded-full shadow-2xl mb-8 animate-bounce">
            <span className="text-3xl animate-pulse">📅</span>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-gold-600 bg-clip-text text-transparent mb-6 tracking-wide">
            Timeline Generator
          </h1>
          
          {/* Floral underline */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 text-gold-400">💖</div>
            </div>
          </div>
          
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed font-light">
            Create a perfectly orchestrated wedding day timeline that ensures every moment flows seamlessly
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          
          {/* Form Section */}
          <div className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-gold-200/50 p-8">
              <div className="flex items-center mb-8">
                <div className="w-12 h-12 bg-gradient-to-r from-rose-400 to-pink-400 rounded-2xl flex items-center justify-center mr-4 shadow-lg animate-pulse">
                  <span className="text-2xl">⚙️</span>
                </div>
                <h3 className="text-2xl font-serif font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
                  Wedding Details
                </h3>
              </div>
              
              <div className="space-y-6">
                {/* Row 1: Wedding Date & Ceremony Time */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Wedding Date */}
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gold-400">
                      <span className="text-xl">📅</span>
                    </div>
                    <input
                      type="date"
                      value={formData.weddingDate}
                      onChange={(e) => handleInputChange('weddingDate', e.target.value)}
                      className="w-full pl-12 pr-4 py-4 border-2 border-champagne-200 rounded-xl focus:ring-4 focus:ring-gold-200 focus:border-gold-400 transition-all duration-300 bg-white/80 font-medium"
                    />
                    <label className="absolute -top-2 left-4 bg-white px-2 text-sm font-medium text-gray-600">
                      Wedding Date
                    </label>
                  </div>

                  {/* Ceremony Time */}
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-rose-400">
                      <span className="text-xl">💒</span>
                    </div>
                    <input
                      type="time"
                      value={formData.ceremonyTime}
                      onChange={(e) => handleInputChange('ceremonyTime', e.target.value)}
                      className="w-full pl-12 pr-4 py-4 border-2 border-champagne-200 rounded-xl focus:ring-4 focus:ring-gold-200 focus:border-gold-400 transition-all duration-300 bg-white/80 font-medium"
                    />
                    <label className="absolute -top-2 left-4 bg-white px-2 text-sm font-medium text-gray-600">
                      Ceremony Time
                    </label>
                  </div>
                </div>

                {/* Row 2: Reception Date & Reception Time */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Reception Date */}
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-purple-400">
                      <span className="text-xl">📅</span>
                    </div>
                    <input
                      type="date"
                      value={formData.receptionDate}
                      onChange={(e) => handleInputChange('receptionDate', e.target.value)}
                      className="w-full pl-12 pr-4 py-4 border-2 border-champagne-200 rounded-xl focus:ring-4 focus:ring-gold-200 focus:border-gold-400 transition-all duration-300 bg-white/80 font-medium"
                    />
                    <label className="absolute -top-2 left-4 bg-white px-2 text-sm font-medium text-gray-600">
                      Reception Date
                    </label>
                  </div>

                  {/* Reception Time */}
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-purple-400">
                      <span className="text-xl">🎉</span>
                    </div>
                    <input
                      type="time"
                      value={formData.receptionTime}
                      onChange={(e) => handleInputChange('receptionTime', e.target.value)}
                      className="w-full pl-12 pr-4 py-4 border-2 border-champagne-200 rounded-xl focus:ring-4 focus:ring-gold-200 focus:border-gold-400 transition-all duration-300 bg-white/80 font-medium"
                    />
                    <label className="absolute -top-2 left-4 bg-white px-2 text-sm font-medium text-gray-600">
                      Reception Time
                    </label>
                  </div>
                </div>

                {/* Elegant divider */}
                <div className="w-full h-px bg-gradient-to-r from-transparent via-gold-300 to-transparent my-6"></div>

                {/* Add Events & Rituals Buttons - 4 options */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <button
                    onClick={() => setShowUnifiedModal({ show: true, type: 'events' })}
                    className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-3 px-4 rounded-xl font-bold hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                  >
                    <span className="mr-2">🎉</span>
                    Select Events
                  </button>
                  <button
                    onClick={() => setShowUnifiedModal({ show: true, type: 'savedRituals' })}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-4 rounded-xl font-bold hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                  >
                    <span className="mr-2">⭐</span>
                    Select Rituals
                  </button>
                  <button
                    onClick={() => setShowCustomEventModal(true)}
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-3 px-4 rounded-xl font-bold hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                  >
                    <span className="mr-2">✨</span>
                    Add Custom Event
                  </button>
                  <button
                    onClick={() => setShowUnifiedModal({ show: true, type: 'allRituals' })}
                    className="bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-4 rounded-xl font-bold hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                  >
                    <span className="mr-2">🎭</span>
                    Add Rituals
                  </button>
                </div>

                {/* Helper Text */}
                {stagedEvents.length === 0 && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-rose-50 via-pink-50 to-gold-50 border-2 border-rose-200 rounded-2xl shadow-md">
                    <p className="text-transparent bg-clip-text bg-gradient-to-r from-rose-600 via-pink-600 to-gold-600 text-sm font-bold flex items-center">
                      <span className="mr-2 text-xl">✨</span>
                      Add events or rituals above to create your magical timeline
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-4 pt-4">
                  <button
                    onClick={() => generateTimeline()}
                    disabled={!formData.ceremonyTime || stagedEvents.length === 0}
                    className="flex-1 bg-gradient-to-r from-rose-500 to-gold-500 text-white py-4 px-6 rounded-xl font-bold hover:from-rose-600 hover:to-gold-600 hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Generating...
                      </div>
                    ) : (
                      <>
                        <span className="mr-2">⚡</span>
                        Generate Timeline
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={clearForm}
                    className="px-6 py-4 border-2 border-gray-300 text-gray-600 rounded-xl font-bold hover:bg-gray-50 hover:shadow-lg transition-all duration-300"
                  >
                    🔄 Clear
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Live Schedule Preview */}
          <div className={`transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-gold-200/50 p-8 sticky top-6">
              <div className="flex items-center mb-8">
                <div className="w-12 h-12 bg-gradient-to-r from-gold-400 to-yellow-400 rounded-2xl flex items-center justify-center mr-4 shadow-lg animate-pulse">
                  <span className="text-2xl">📋</span>
                </div>
                <h3 className="text-2xl font-serif font-bold bg-gradient-to-r from-gold-600 to-yellow-600 bg-clip-text text-transparent">
                  Wedding Timeline
                </h3>
              </div>

              {/* Helper Text for Timeline Interaction */}
              {timeline.length > 0 && (
                <div className="mb-4 p-4 bg-gradient-to-r from-purple-50 via-pink-50 to-rose-50 border-2 border-purple-200 rounded-2xl shadow-md">
                  <p className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 text-sm font-bold flex items-center">
                    <span className="mr-2 text-xl">💫</span>
                    Change sequence number to reorder • Drag cards • Click time to edit
                  </p>
                </div>
              )}

              {timeline.length === 0 ? (
                <div className="text-center py-16">
                  <div className="text-7xl mb-6">⏰</div>
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-gold-600 to-yellow-600 bg-clip-text text-transparent mb-3">
                    Ready to Plan Your Perfect Day?
                  </h3>
                  <p className="text-gray-600 mb-2">
                    Fill in your wedding details and click "Generate Timeline"
                  </p>
                  <p className="text-gray-500 text-sm">
                    We'll create a perfectly timed schedule for your big day! ✨
                  </p>
                </div>
              ) : (
                <>
                  <DraggableTimeline
                    events={timeline}
                    onReorder={handleReorderTimeline}
                    onEdit={handleEditEvent}
                    onDelete={handleDeleteEvent}
                  />

                  <div className="space-y-3 mt-8 pt-6 border-t border-gold-200">
                    <button
                      onClick={handleSaveClick}
                      className="w-full bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-3 px-4 rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-300 animate-pulse"
                    >
                      <span className="mr-2">💾</span>
                      Save Timeline
                    </button>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={downloadPDF}
                        className="bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 px-4 rounded-xl font-bold hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                      >
                        <span className="mr-2">📥</span>
                        Download PDF
                      </button>
                      
                      <button
                        onClick={handleViewSavedClick}
                        className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-3 px-4 rounded-xl font-bold hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                      >
                        <span className="mr-2">📋</span>
                        View Saved
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Luxury Footer */}
        <div className={`mt-16 text-center transition-all duration-700 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="w-32 h-px bg-gradient-to-r from-transparent via-gold-400 to-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-light italic">
            Part of your personalized VowAI Wedding Journey
          </p>
        </div>
      </div>

      {/* Modals */}
      <CustomEventModal
        isOpen={showCustomEventModal}
        onClose={() => setShowCustomEventModal(false)}
        onSave={handleAddCustomEvent}
      />
      
      <UnifiedSelectorModal
        isOpen={showUnifiedModal.show}
        type={showUnifiedModal.type}
        onClose={() => setShowUnifiedModal({ show: false, type: null })}
        onSelect={handleAddFromUnifiedModal}
        existingItems={stagedEvents}
      />

      <SaveTimelineModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={handleSaveTimeline}
        formData={formData}
        timeline={timeline}
        bufferSettings={bufferSettings}
      />

      <style jsx>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .animate-fadeInScale {
          animation: fadeInScale 0.6s ease-out;
        }
        
        .delay-200 { animation-delay: 200ms; }
        .delay-400 { animation-delay: 400ms; }
        .delay-600 { animation-delay: 600ms; }
        .delay-1000 { animation-delay: 1000ms; }
        .delay-1500 { animation-delay: 1500ms; }
        .delay-2000 { animation-delay: 2000ms; }
      `}</style>

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.show}
        onClose={() => setNotification({ ...notification, show: false })}
        message={notification.message}
        type={notification.type}
      actionLabel={notification.actionLabel}
      onAction={notification.onAction}
        actionText={notification.actionText}
        onAction={notification.onAction}
      />

      {/* Edit Event Modal */}
      <EditEventModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingEvent(null);
          setEditingIndex(null);
        }}
        event={editingEvent}
        onSave={handleSaveEdit}
      />
    </div>
  );
};

export default TimelineGenerator;
